import { Button, Divider, message, notification, Typography, Upload, UploadProps } from "antd";
import { RcFile } from "antd/es/upload";
import { api } from "api";
import { ApiService } from "api/api.service";
import { T_Api_Success_Res } from "api/api.types";
import { IconDocx, IconPdf } from "assets/icons/onboarding";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import OnboardingLayout from "layout/OnboardingLayout";
import { Fragment, ReactElement, useState } from "react";
import { TbChevronRight, TbFileFilled, TbFileText, TbTrash } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { AvailableResources } from "types/permissionGroup.types";
import { MAX_FILE_UPLOAD_SIZE } from "utils/constants";
import { delay, getErrorMessage, humanFileSize } from "utils/functions";
import { storedTokens } from "utils/storedTokens";
import ApplicationRedirect from "../components/ApplicationRedirect";
import styles from "../Onboarding.module.scss";
import { queryClient } from "utils/queryClient";
import CounterText from "components/CounterText";
import { ONBOARDING_ROUTES } from "utils/routes";
import PageTitle from "components/PageTitle";
import { SingleQueryWrapper } from "components/QueryWrapper";
import DashboardLayout from "layout/DashboardLayout";

const extensionIcons: Record<string, ReactElement> = {
  docx: <IconDocx />,
  pdf: <IconPdf />,
};

export function UploadLoanDocs() {
  const navigate = useNavigate();
  const applicationId = storedTokens.selectedApplicationId.getToken();
  const allLoans = ApiService.onboarding.useGetArtifacts(applicationId, "loans");

  const [isLoading, setIsLoading] = useState(false);
  const [fileList, setFileList] = useState<RcFile[]>([]);
  // const [data, setData] = useState<any[]>([]);
  const [uploadPercent, setUploadPercent] = useState<number>();
  const hasExisting =
    allLoans?.data &&
    allLoans?.data?.result?.length > 0 &&
    allLoans?.data?.result?.map((el) => el.attachments).flat().length > 0;

  // const [progress, setProgress] = useState<Record<string, number>>({});

  const commitLoan = ApiService.onboarding.useCommitArtifact("loans");
  const props: UploadProps = {
    name: "files",
    accept: ".pdf",
    multiple: true,
    beforeUpload: (file) => {
      const isLtMaxSize = file.size / 1024 / 1024 < MAX_FILE_UPLOAD_SIZE;
      const isSameFile = fileList.find((el) => {
        return el.name === file.name && el.size === file.size;
      });
      if (!isLtMaxSize) {
        message.error(`File must smaller than ${MAX_FILE_UPLOAD_SIZE}MB!`);
      } else if (isSameFile) {
        message.error("File with same name and same size already exists. Please rename the file and re-upload");
      } else {
        setFileList((prev) => [...prev, file]);
      }

      return Upload.LIST_IGNORE;
    },
  };

  if (!applicationId) {
    return <ApplicationRedirect />;
  }

  return (
    <DashboardLayout>
      <OnboardingLayout loader={{ isLoading: isLoading }}>
        <PageTitle pageTitle={[]} title="Upload Loan Docs - 1" />
        <div className="flex flex-col gap-8 max-w-[900px] mx-auto p-5">
          {fileList.length ? (
            <div className="flex items-center justify-between">
              <Typography.Title className="!mb-0" level={5}>
                New files
              </Typography.Title>
              <Upload {...props}>
                <Button icon={<TbFileText />} variant="filled" color="primary" className="border-purple-300">
                  Upload Files
                </Button>
              </Upload>
            </div>
          ) : (
            <Upload.Dragger className={styles.file_upload} showUploadList={false} {...props}>
              <div className="bg-white flex flex-col items-center justify-center">
                <img role="presentation" className="max-w-[400px]" src="/doc-upload.png" />
                <Typography.Title level={5}>Tell us about your loans</Typography.Title>
                <Typography.Text type="secondary">
                  Drag & drop loan agreements. Max size {MAX_FILE_UPLOAD_SIZE}MB. Supported file types .pdf
                </Typography.Text>
                <Button className="mt-4">Select Files</Button>
              </div>
            </Upload.Dragger>
          )}

          <div className="flex flex-col gap-3 mb-[98px]">
            {fileList.map((file) => {
              const x = file.name?.split(".");
              const extension = x[x?.length - 1];
              const fileName = file?.name;

              return (
                <div
                  key={file.uid}
                  className="bg-white rounded-xl border border-solid border-gray-300 flex items-center gap-3 p-3"
                >
                  <div className="text-3xl">{extensionIcons[extension]}</div>
                  <div className="flex gap-3 justify-between w-full">
                    <div className="">
                      <Typography.Title level={5} className="!mb-1">
                        {fileName}
                      </Typography.Title>
                      <Typography.Text type="secondary">{humanFileSize(file?.size ?? 0)}</Typography.Text>
                    </div>
                    {/* {progress?.[file?.uid] ? <Progress percent={progress?.[file?.uid]} /> : null} */}
                  </div>
                  <Button
                    variant="text"
                    color="danger"
                    danger
                    icon={<TbTrash />}
                    onClick={() => {
                      setFileList((prev) => {
                        return prev.filter((el) => el.uid !== file.uid);
                      });
                    }}
                  />
                </div>
              );
            })}

            <SingleQueryWrapper
              query={allLoans}
              showLoaderOn="isFetching"
              renderData={(loans) => {
                return (
                  <>
                    {hasExisting ? (
                      <>
                        <Typography.Title className="mt-4" level={5}>
                          Existing files
                        </Typography.Title>
                        {loans?.result?.map((loan) => {
                          return (
                            <Fragment key={loan._id}>
                              {loan.attachments.map((file) => {
                                const x = file.original_file_name?.split(".");
                                const extension = x[x?.length - 1];
                                const fileName = file.original_file_name;
                                return (
                                  <div
                                    key={file._id}
                                    className="bg-white rounded-xl border border-solid border-gray-300 flex items-center gap-3 p-3"
                                  >
                                    <div className="text-3xl">{extensionIcons[extension]}</div>
                                    <div className="flex gap-3 justify-between w-full">
                                      <div className="">
                                        <Typography.Title level={5} className="!mb-1">
                                          {fileName}
                                        </Typography.Title>
                                        <Typography.Text type="secondary">
                                          {humanFileSize(file?.size ?? 0)}
                                        </Typography.Text>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </Fragment>
                          );
                        })}
                      </>
                    ) : null}
                    {!hasExisting && fileList.length === 0 ? (
                      <div className="flex flex-col gap-4">
                        <Divider>Or</Divider>
                        <Button
                          onClick={() => navigate(ONBOARDING_ROUTES.UploadLoanDocs_2)}
                          variant="filled"
                          color="primary"
                          className="border-purple-300"
                          icon={<TbFileFilled />}
                        >
                          Add Creditor Manually
                        </Button>
                      </div>
                    ) : null}
                  </>
                );
              }}
            />
          </div>
        </div>

        {fileList.length || hasExisting ? (
          <div className="fixed left-0 right-0 bottom-0 flex justify-center bg-white py-3 px-5 border-t border-solid border-[0px] border-gray-300 ">
            <Button
              size="large"
              type="primary"
              icon={<TbChevronRight />}
              iconPosition="end"
              className="w-fit"
              loading={commitLoan.isPending || isLoading}
              onClick={() => {
                if (fileList.length > 0) {
                  setIsLoading(true);
                  const formData = new FormData();
                  fileList.forEach((file) => {
                    formData.append("files", file);
                  });
                  formData.append("category", AvailableResources.LOAN);

                  const config: AxiosRequestConfig = {
                    // handles the upload progress
                    onUploadProgress: (event) => {
                      const percent = Math.floor((event.loaded / (event?.total ?? 1000)) * 100);
                      setUploadPercent(percent);
                    },
                  };

                  // post request to the api
                  api
                    .post("/file/upload", formData, config)
                    .then((res: AxiosResponse<T_Api_Success_Res<{ attachments: string[] }>>) => {
                      const resFile = res.data.result;
                      // message.success("File upload successful");
                      commitLoan.mutate(
                        {
                          applicationId: applicationId,
                          body: {
                            attachments: resFile?.attachments,
                          },
                        },
                        {
                          onSuccess: (data) => {
                            const prevLoanCount = allLoans.data?.result?.length ?? 0;
                            // if (data.result.summary.processed === fileList.length) {
                            //   setIsLoading(false);
                            //   navigate(ONBOARDING_ROUTES.UploadLoanDocs_2);
                            // }

                            return delay(data.result.refreshInMilis, async () => {
                              await queryClient.invalidateQueries({
                                queryKey: ["application", "artifact/all", "loans", applicationId],
                              });
                              const newLoanCount = allLoans.data?.result?.length ?? 0;

                              if (newLoanCount > prevLoanCount) {
                                setIsLoading(false);
                                navigate(ONBOARDING_ROUTES.UploadLoanDocs_2);
                              } else {
                                notification.info({
                                  message: `Loan documents still processing`,
                                  description: (
                                    <>
                                      Please wait till we update all files. You can reload to check progress.Redirecting
                                      to next step in <CounterText seconds={3} /> seconds.
                                    </>
                                  ),
                                });
                                await delay(3000, () => {
                                  setIsLoading(false);
                                  navigate(ONBOARDING_ROUTES.UploadLoanDocs_2);
                                });
                              }
                            });
                          },
                          onError: () => {
                            setIsLoading(false);
                          },
                        },
                      );
                    })
                    .catch(() => {
                      setIsLoading(false);
                    });
                } else if (hasExisting && fileList.length === 0) {
                  navigate(ONBOARDING_ROUTES.UploadLoanDocs_2);
                }
              }}
            >
              {/* {uploadPercent && uploadPercent !== 100 ? (
              <Progress percent={uploadPercent} type="circle" size={24} />
            ) : null} */}
              <span>Continue</span>
            </Button>
          </div>
        ) : null}
      </OnboardingLayout>
    </DashboardLayout>
  );
}
